import { Box, Grid, Typography } from '@mui/material';
import { SVG } from 'components/Asset/SVG';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import { truncateTo2dp } from 'utils/helpers/general';
import { type FleetDeviceDetailsPerDay } from 'types/fuelAnalytics';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useAppSelector } from 'store/hook';
import { type Asset } from 'types/asset.types';
import { IgnitionStatusIndicator } from 'components/StatusIndicator';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  data: FleetDeviceDetailsPerDay;
}

const FuelAssetHeader = ({ data }: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { unitSystem } = useCustomerInfo();
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const { assets } = useAppSelector((state) => state.assetReducer);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);
  return (
    <Box
      sx={{
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <SVG
        name={mapVehiclesAssets(data.asset_type)}
        style={{
          fill: '#E2C171',
          height: '30px',
          width: '60px',
          paddingRight: '10px',
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '13px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'left',
                }}
              >
                <IgnitionStatusIndicator
                  latestTagData={latestTagData}
                  asset={
                    assetsWithLiveData.find(
                      (asset) => asset.bumperNumber === data.bumper_id
                    ) as Asset
                  }
                  size={10}
                  marginRight="5px"
                />
                {data.bumper_id}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* <PowerSettingsNewIcon
                sx={{
                  color: '#00C853',
                  marginRight: '8px',
                  fontSize: '12px',
                }}
              /> */}
              <Typography
                sx={{
                  fontSize: '10px',
                  color: palette.textPrimary,
                }}
              >
                {data.make} - {data.model}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {t('avg_fuel_rate')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {formatNumberToShortScale(
                truncateTo2dp(Number(data?.avg_fuel_rate)),
                2
              )}
              {unitSystem === 'metric' ? 'L/Hr' : 'G/Hr'}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {t('co2_emmission')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {formatNumberToShortScale(
                truncateTo2dp(Number(data?.co2_emission)),
                2
              )}
              {t('fuel_usage_unit_kg')}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {t('total_fuel_consumed')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {formatNumberToShortScale(
                truncateTo2dp(Number(data?.total_fuel_consumption)),
                2
              )}
              {unitSystem === 'metric' ? 'L' : 'G'}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {t('no_of_idle_events')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {data?.num_of_idle_events}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FuelAssetHeader;
